import { useMemo } from 'react'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import type { BrowseMatterFilesProps } from '@/user_files/components/BrowseMatterFiles'
import { useBrowseFiles } from '@/user_files/components/useBrowseFiles'
import { useMatterExternalSyncStatus } from '@/user_files/external-storage/hooks/useMatterExternalSyncStatus'
import { filterVisibleExternalStorageItems } from '@/user_files/external-storage/utils/external-storage-items'

type UseExternalFilesTabParams = {
  active: boolean
  fallbackBreadcrumbItems: BreadcrumbItemType[]
  matterId: string
  open: boolean
}

type ExternalFilesBrowseProps = Pick<
  BrowseMatterFilesProps,
  'blockingError' | 'breadcrumbItems' | 'files' | 'folders' | 'loading' | 'onNavigateToFolder' | 'onRetry'
>

export function useExternalFilesTab({ active, fallbackBreadcrumbItems, matterId, open }: UseExternalFilesTabParams) {
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const externalSyncStatus = useMatterExternalSyncStatus({
    matterId,
    enabled: open && driveSyncEnabled,
  })

  const activeExternalMapping = externalSyncStatus.activeMapping
  const syncedProvider = externalSyncStatus.syncedProvider
  const syncedProviderLabel = syncedProvider ? kExternalStorageProviderDisplayName[syncedProvider] : null
  const showExternalTab = driveSyncEnabled && !!activeExternalMapping && !!syncedProvider && !!syncedProviderLabel

  const externalFilesBrowse = useBrowseFiles({
    matterId,
    open: open && showExternalTab && active && !!activeExternalMapping,
    initialFolderId: activeExternalMapping?.user_folder_id,
    initialFolderRequired: true,
    refreshOnOpen: true,
  })

  const externalFolders = useMemo(
    () => filterVisibleExternalStorageItems(externalFilesBrowse.folders, syncedProvider),
    [externalFilesBrowse.folders, syncedProvider]
  )
  const externalFiles = useMemo(() => filterVisibleExternalStorageItems(externalFilesBrowse.files, syncedProvider), [externalFilesBrowse.files, syncedProvider])

  const browseProps: ExternalFilesBrowseProps = {
    breadcrumbItems: externalFilesBrowse.breadcrumbItems.length > 0 ? externalFilesBrowse.breadcrumbItems : fallbackBreadcrumbItems,
    blockingError: externalSyncStatus.error ? 'Unable to load files.' : externalFilesBrowse.error,
    files: activeExternalMapping ? externalFiles : [],
    folders: activeExternalMapping ? externalFolders : [],
    loading: externalSyncStatus.loading || (!!activeExternalMapping && externalFilesBrowse.loading),
    onNavigateToFolder: externalFilesBrowse.onNavigateToFolder,
    onRetry: externalSyncStatus.error
      ? () => {
          externalSyncStatus.reload()
        }
      : externalFilesBrowse.retry,
  }

  return {
    activeExternalMapping,
    browseProps,
    externalFiles,
    externalFolders,
    externalSyncStatus,
    showExternalTab,
    syncedProvider,
    syncedProviderLabel,
  }
}
